/*
Price development chart (median kr/m2 per quarter, rolling 4 quarters).

initPriceChart(canvasId, labels, ticks, series, events)
  labels: quarter display labels, one per x position, pre-translated server-side
  ticks:  parallel array with the year for Q1 positions and '' elsewhere (sparse x axis)
  series: [{key: 'apartment'|'house'|'cottage', label: str, prices: [int|null], counts: [int|null]}]
  events: [{x: label index, y: kr/m2, type: 'registration'|'ad', label: pre-formatted tooltip}]

initMarketChart(canvasId, payload)
  payload: {labels, ticks, premium: [float|null] in percent units, premium_counts,
            days: [int|null], days_counts} — see market_chart_payload

initDemographicsChart(canvasId, payload)
  payload: {buckets: [age label], series: [{key: 'region'|'municipality'|'country',
            label: str, shares: [float]}]} — see demographics_chart_payload

initSaleScatterChart(canvasId, payload)
  payload: {points: [{x: ask kr, y: sold kr, url, label}], max} — see sale_scatter_payload
  One dot per sale with a y=x diagonal; clicking a dot opens the home's page.

initHousingChart(canvasId, payload)
  payload: {slices: [{key, label, count, share}], total} — see housing_chart_payload
  Boligmasse doughnut; the legend labels carry the shares.

initBarChart(canvasId, payload)
  payload: {labels: [str], titles: [str] (optional, full date per bucket for the tooltip),
            series: [{key, label, values: [int]}]} — see bar_chart_payload
  Generic counts-per-bucket bars (admin pages); the legend shows for more than one series.

Palette validated (CVD-safe, dataviz six checks) against the light surface.
*/

const PRICE_CHART_COLORS = {
  apartment: '#1669E0',
  house: '#E8871E',
  cottage: '#12805C',
};
const PRICE_CHART_INK = '#21262E';
const PRICE_CHART_MUTED = '#6B7280';
const PRICE_CHART_GRID = 'rgba(33, 38, 46, 0.08)';

const MARKET_CHART_COLORS = {
  premium: '#4A3AA7',
  days: '#C2417F',
};

// Fixed by series identity: the region always wears the site blue
const DEMOGRAPHICS_CHART_COLORS = {
  region: '#1669E0',
  municipality: '#E8871E',
  country: '#12805C',
};

// Fixed by series identity, shared by the admin bar charts
const BAR_CHART_COLORS = {
  candidates: PRICE_CHART_COLORS.apartment,
  parsed: PRICE_CHART_COLORS.house,
  users: PRICE_CHART_COLORS.apartment,
  favorites: PRICE_CHART_COLORS.house,
  saved_searches: PRICE_CHART_COLORS.apartment,
};

const initBarChart = (canvasId, payload) => {
  const canvas = document.getElementById(canvasId);
  if (!canvas || typeof Chart === 'undefined') {
    return;
  }

  const datasets = payload.series.map((serie) => ({
    label: serie.label,
    data: serie.values,
    backgroundColor: BAR_CHART_COLORS[serie.key] || PRICE_CHART_INK,
    borderRadius: 3,
    categoryPercentage: 0.8,
    barPercentage: 0.9,
  }));

  new Chart(canvas, {
    type: 'bar',
    data: {labels: payload.labels, datasets: datasets},
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {mode: 'index', intersect: false},
      plugins: {
        legend: {
          display: datasets.length > 1,
          labels: {color: PRICE_CHART_INK, boxWidth: 10, boxHeight: 10, font: {size: 12}},
        },
        tooltip: {
          callbacks: {
            title: (items) => (payload.titles && items.length ? payload.titles[items[0].dataIndex] : items[0].label),
            label: (context) => context.dataset.label + ': ' + new Intl.NumberFormat('nb-NO').format(context.parsed.y),
          },
        },
      },
      scales: {
        x: {
          grid: {display: false},
          ticks: {color: PRICE_CHART_MUTED, autoSkip: true, maxTicksLimit: 12, maxRotation: 0},
        },
        y: {
          beginAtZero: true,
          grid: {color: PRICE_CHART_GRID},
          border: {display: false},
          ticks: {color: PRICE_CHART_MUTED, precision: 0, maxTicksLimit: 6},
        },
      },
    },
  });
};

const formatKrM2 = (value) => new Intl.NumberFormat('nb-NO').format(value) + ' kr/m²';
const formatPremium = (value) => new Intl.NumberFormat('nb-NO', {signDisplay: 'exceptZero', minimumFractionDigits: 1, maximumFractionDigits: 1}).format(value) + ' %';

const initPriceChart = (canvasId, labels, ticks, series, events) => {
  const canvas = document.getElementById(canvasId);
  if (!canvas || typeof Chart === 'undefined') {
    return;
  }

  const datasets = series.map((serie) => ({
    type: 'line',
    label: serie.label,
    data: serie.prices,
    counts: serie.counts,
    borderColor: PRICE_CHART_COLORS[serie.key] || PRICE_CHART_INK,
    backgroundColor: PRICE_CHART_COLORS[serie.key] || PRICE_CHART_INK,
    borderWidth: 2,
    pointStyle: 'line',  // Legend swatch stays a line while the event datasets show their marker shapes
    pointRadius: 0,
    pointHoverRadius: 4,
    pointHitRadius: 12,
    spanGaps: false,
    tension: 0.15,
  }));

  const registrations = events.filter((event) => event.type === 'registration');
  const ads = events.filter((event) => event.type === 'ad');
  const eventDataset = (items, label, filled) => ({
    type: 'scatter',
    label: label,
    // The category x axis resolves points by label string, not by numeric index
    data: items.map((event) => ({x: labels[event.x], y: event.y, eventKey: event.key})),
    pointStyle: filled ? 'circle' : 'crossRot',
    radius: 5,
    hoverRadius: 7,
    backgroundColor: filled ? PRICE_CHART_INK : 'rgba(0, 0, 0, 0)',
    borderColor: PRICE_CHART_INK,
    borderWidth: 2,
  });
  if (registrations.length > 0) {
    datasets.push(eventDataset(registrations, canvas.dataset.labelSales || 'Solgt', true));
  }
  if (ads.length > 0) {
    datasets.push(eventDataset(ads, canvas.dataset.labelAds || 'Annonse', false));
  }

  // Hovering an event point highlights the matching row in the sale history table
  const highlightHistoryRow = (elements) => {
    let key = null;
    elements.forEach((element) => {
      const raw = element.element && element.element.$context && element.element.$context.raw;
      if (raw && raw.eventKey) {
        key = raw.eventKey;
      }
    });
    document.querySelectorAll('[data-event-key]').forEach((row) => {
      row.classList.toggle('event-highlight', key !== null && row.dataset.eventKey === key);
    });
  };

  const chart = new Chart(canvas, {
    data: {labels: labels, datasets: datasets},
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {mode: 'nearest', intersect: false},
      onHover: (event, elements) => highlightHistoryRow(elements),
      plugins: {
        legend: {
          display: datasets.length > 1,
          labels: {color: PRICE_CHART_INK, usePointStyle: true, pointStyleWidth: 14, boxHeight: 8, font: {size: 12}},
        },
        tooltip: {
          // Event points get no tooltip; hovering them highlights the sale history row instead
          filter: (item) => !(item.raw && item.raw.eventKey),
          callbacks: {
            label: (context) => {
              const counts = context.dataset.counts;
              const count = counts && counts[context.dataIndex];
              return context.dataset.label + ': ' + formatKrM2(context.parsed.y) + (count ? ' · ' + count + ' ' + (canvas.dataset.labelCount || 'salg') : '');
            },
          },
        },
      },
      scales: {
        x: {
          grid: {display: false},
          ticks: {
            color: PRICE_CHART_MUTED,
            autoSkip: false,
            maxRotation: 0,
            callback: (value, index) => ticks[index] || null,
          },
        },
        y: {
          grid: {color: PRICE_CHART_GRID},
          border: {display: false},
          ticks: {color: PRICE_CHART_MUTED, maxTicksLimit: 6, callback: (value) => formatKrM2(value)},
        },
      },
    },
  });

  // The reverse of highlightHistoryRow: hovering a sale history row lights up its event point
  document.querySelectorAll('[data-event-key]').forEach((row) => {
    const targets = [];
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      dataset.data.forEach((point, index) => {
        if (point && point.eventKey === row.dataset.eventKey) {
          targets.push({datasetIndex: datasetIndex, index: index});
        }
      });
    });
    if (targets.length === 0) {
      return;
    }
    row.addEventListener('mouseenter', () => {
      chart.setActiveElements(targets);
      chart.update();
    });
    row.addEventListener('mouseleave', () => {
      chart.setActiveElements([]);
      chart.update();
    });
  });
};

const formatShare = (value) => new Intl.NumberFormat('nb-NO', {maximumFractionDigits: 1}).format(value) + ' %';
const formatMill = (value) => (value ? new Intl.NumberFormat('nb-NO', {maximumFractionDigits: 1}).format(value / 1000000) + ' mill' : '0');
const formatKr = (value) => new Intl.NumberFormat('nb-NO').format(value) + ' kr';

// Fixed by bucket identity; "other" deliberately wears the neutral gray
const HOUSING_CHART_COLORS = {
  detached: '#1669E0',
  semi_detached: '#4A3AA7',
  row: '#E8871E',
  apartment: '#12805C',
  other: '#6B7280',
};

const initHousingChart = (canvasId, payload) => {
  const canvas = document.getElementById(canvasId);
  if (!canvas || typeof Chart === 'undefined') {
    return;
  }

  new Chart(canvas, {
    type: 'doughnut',
    data: {
      labels: payload.slices.map((slice) => slice.label + ' ' + slice.share + ' %'),
      datasets: [{
        data: payload.slices.map((slice) => slice.count),
        backgroundColor: payload.slices.map((slice) => HOUSING_CHART_COLORS[slice.key] || PRICE_CHART_MUTED),
        borderColor: '#fff',  // 2px surface gap between the slices
        borderWidth: 2,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '62%',
      plugins: {
        legend: {
          position: 'right',
          labels: {color: PRICE_CHART_INK, usePointStyle: true, pointStyleWidth: 14, boxHeight: 8, font: {size: 12}},
        },
        tooltip: {
          callbacks: {
            label: (context) => ' ' + new Intl.NumberFormat('nb-NO').format(context.parsed) + ' ' + (canvas.dataset.labelHomes || 'boliger'),
          },
        },
      },
    },
  });
};

const initSaleScatterChart = (canvasId, payload) => {
  const canvas = document.getElementById(canvasId);
  if (!canvas || typeof Chart === 'undefined') {
    return;
  }

  const bound = payload.max * 1.03;  // Headroom so edge dots are not clipped
  const datasets = [
    {
      type: 'line',
      data: [{x: 0, y: 0}, {x: bound, y: bound}],  // The y=x "sold at asking" reference
      borderColor: PRICE_CHART_INK,
      borderWidth: 1,
      pointRadius: 0,
      pointHitRadius: 0,
    },
    {
      type: 'scatter',
      data: payload.points,
      backgroundColor: 'rgba(22, 105, 224, 0.55)',  // Semi-transparent so dense areas read
      radius: 3.5,
      hoverRadius: 6,
      hitRadius: 6,
      hoverBackgroundColor: PRICE_CHART_COLORS.apartment,
    },
  ];

  const pointAt = (elements) => {
    const element = elements.find((el) => el.datasetIndex === 1);
    return element ? payload.points[element.index] : null;
  };

  new Chart(canvas, {
    data: {datasets: datasets},
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {mode: 'nearest', intersect: true},
      onClick: (event, elements) => {
        const point = pointAt(elements);
        if (point && point.url) {
          window.location.href = point.url;
        }
      },
      onHover: (event, elements) => {
        event.native.target.style.cursor = pointAt(elements) ? 'pointer' : 'default';
      },
      plugins: {
        legend: {display: false},
        tooltip: {
          filter: (item) => item.datasetIndex === 1,
          callbacks: {
            title: (items) => (items.length ? items[0].raw.label : ''),
            label: (context) => [
              (canvas.dataset.labelAsk || 'Prisantydning') + ': ' + formatKr(context.parsed.x),
              (canvas.dataset.labelSold || 'Salgspris') + ': ' + formatKr(context.parsed.y),
            ],
          },
        },
      },
      scales: {
        x: {
          type: 'linear',
          min: 0,
          max: bound,
          grid: {display: false},
          title: {display: true, text: canvas.dataset.labelAsk || 'Prisantydning', color: PRICE_CHART_MUTED, font: {size: 12}},
          ticks: {color: PRICE_CHART_MUTED, maxTicksLimit: 7, callback: (value) => formatMill(value)},
        },
        y: {
          type: 'linear',
          min: 0,
          max: bound,
          grid: {color: PRICE_CHART_GRID},
          border: {display: false},
          title: {display: true, text: canvas.dataset.labelSold || 'Salgspris', color: PRICE_CHART_MUTED, font: {size: 12}},
          ticks: {color: PRICE_CHART_MUTED, maxTicksLimit: 6, callback: (value) => formatMill(value)},
        },
      },
    },
  });
};

const initDemographicsChart = (canvasId, payload) => {
  const canvas = document.getElementById(canvasId);
  if (!canvas || typeof Chart === 'undefined') {
    return;
  }

  const datasets = payload.series.map((serie) => ({
    label: serie.label,
    data: serie.shares,
    backgroundColor: DEMOGRAPHICS_CHART_COLORS[serie.key] || PRICE_CHART_INK,
    borderRadius: 4,
    categoryPercentage: 0.72,
    barPercentage: 0.85,
  }));

  new Chart(canvas, {
    type: 'bar',
    data: {labels: payload.buckets, datasets: datasets},
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {mode: 'index', intersect: false},
      plugins: {
        legend: {
          display: datasets.length > 1,
          labels: {color: PRICE_CHART_INK, boxWidth: 10, boxHeight: 10, font: {size: 12}},
        },
        tooltip: {
          callbacks: {
            label: (context) => context.dataset.label + ': ' + formatShare(context.parsed.y),
          },
        },
      },
      scales: {
        x: {
          grid: {display: false},
          ticks: {color: PRICE_CHART_MUTED, maxRotation: 0},
          title: {display: true, text: canvas.dataset.labelAge || 'Alder', color: PRICE_CHART_MUTED, font: {size: 12}},
        },
        y: {
          beginAtZero: true,
          grid: {color: PRICE_CHART_GRID},
          border: {display: false},
          ticks: {color: PRICE_CHART_MUTED, maxTicksLimit: 6, callback: (value) => formatShare(value)},
        },
      },
    },
  });
};

const initMarketChart = (canvasId, payload) => {
  const canvas = document.getElementById(canvasId);
  if (!canvas || typeof Chart === 'undefined') {
    return;
  }

  const lineDataset = (label, data, counts, countLabel, color, axis) => ({
    type: 'line',
    label: label,
    data: data,
    counts: counts,
    countLabel: countLabel,
    yAxisID: axis,
    borderColor: color,
    backgroundColor: color,
    borderWidth: 2,
    pointStyle: 'line',
    pointRadius: 0,
    pointHoverRadius: 4,
    pointHitRadius: 12,
    spanGaps: false,
    tension: 0.15,
  });
  const datasets = [
    lineDataset(canvas.dataset.labelPremium || 'Over/under prisantydning', payload.premium, payload.premium_counts, canvas.dataset.labelSales || 'salg', MARKET_CHART_COLORS.premium, 'y'),
    lineDataset(canvas.dataset.labelDays || 'Dager på markedet', payload.days, payload.days_counts, canvas.dataset.labelAds || 'annonser', MARKET_CHART_COLORS.days, 'yDays'),
  ];

  new Chart(canvas, {
    data: {labels: payload.labels, datasets: datasets},
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {mode: 'nearest', intersect: false},
      plugins: {
        legend: {
          display: true,
          labels: {color: PRICE_CHART_INK, usePointStyle: true, pointStyleWidth: 14, boxHeight: 8, font: {size: 12}},
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.datasetIndex === 0 ? formatPremium(context.parsed.y) : context.parsed.y;
              const count = context.dataset.counts && context.dataset.counts[context.dataIndex];
              return context.dataset.label + ': ' + value + (count ? ' · ' + count + ' ' + context.dataset.countLabel : '');
            },
          },
        },
      },
      scales: {
        x: {
          grid: {display: false},
          ticks: {
            color: PRICE_CHART_MUTED,
            autoSkip: false,
            maxRotation: 0,
            callback: (value, index) => payload.ticks[index] || null,
          },
        },
        y: {
          // The zero line reads as the "sold at asking" baseline, so draw it stronger
          grid: {color: (context) => (context.tick && context.tick.value === 0 ? PRICE_CHART_MUTED : PRICE_CHART_GRID)},
          border: {display: false},
          ticks: {color: PRICE_CHART_MUTED, maxTicksLimit: 6, callback: (value) => formatPremium(value)},
        },
        yDays: {
          position: 'right',
          beginAtZero: true,
          grid: {drawOnChartArea: false},
          border: {display: false},
          ticks: {color: PRICE_CHART_MUTED, maxTicksLimit: 6},
        },
      },
    },
  });
};
